/**
 * Career Memory Service
 *
 * Manages career experiences and timeline data
 */

import { CareerExperience, TimelineEntry } from '../types/careerMemory';
import { getAuth } from 'firebase/auth';
import { getFunctions } from 'firebase/functions';
import { getEmulatorHost, isUsingEmulators } from './emulator';

async function getBaseUrl() {
  const functions = getFunctions();
  const projectId =
    functions.app.options.projectId ||
    process.env.REACT_APP_FIREBASE_PROJECT_ID ||
    'postmortem-pheonix';
  const region = functions.region || 'us-central1';
  return isUsingEmulators()
    ? `http://${getEmulatorHost()}:${process.env.REACT_APP_FUNCTIONS_EMULATOR_PORT || '5001'}/${projectId}/${region}`
    : functions.customDomain || `https://${region}-${projectId}.cloudfunctions.net`;
}

export async function getCareerExperiences(): Promise<CareerExperience[]> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Must be authenticated');

  const idToken = await user.getIdToken(isUsingEmulators());
  const baseUrl = await getBaseUrl();

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 20000);

  try {
    const res = await fetch(`${baseUrl}/getExperiences`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `HTTP ${res.status}`);
    }

    const data = await res.json();
    return Array.isArray(data.experiences) ? data.experiences as CareerExperience[] : [];
  } catch (err: any) {
    clearTimeout(timeoutId);
    if (err.name === 'AbortError') throw new Error('Request timed out');
    throw err;
  }
}

interface ExperienceMutationPayload {
  title?: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  company?: string;
  role?: string;
  type?: string;
  situation?: string;
  task?: string;
  action?: string;
  result?: string;
  skills?: string[];
  technologies?: string[];
  metrics?: string[];
  tags?: string[];
}

export interface ProposedCareerExperience {
  title: string;
  description: string;
  type?: CareerExperience['type'];
  company?: string;
  role?: string;
  startDate?: string;
  endDate?: string;
  situation?: string;
  task?: string;
  action?: string;
  result?: string;
  skills?: string[];
  technologies?: string[];
  metrics?: string[];
}

export interface DeduplicateMergedPayload extends ProposedCareerExperience {}

export interface InternalDuplicateGroup {
  keepId: string;
  removeIds: string[];
  merged: DeduplicateMergedPayload;
}

export interface CrossMatch {
  existingId: string;
  proposedIndex: number;
  merged: DeduplicateMergedPayload;
}

export interface DeduplicateResult {
  internalGroups: InternalDuplicateGroup[];
  crossMatches: CrossMatch[];
  newProposedIndices: number[];
  skipped?: boolean;
}

async function callExperienceApi<T>(
  path: string,
  method: 'GET' | 'POST',
  body?: unknown,
  timeoutMs: number = 20000
): Promise<T> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Must be authenticated');

  const idToken = await user.getIdToken(isUsingEmulators());
  const baseUrl = await getBaseUrl();

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const res = await fetch(`${baseUrl}/${path}`, {
      method,
      headers: {
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: body ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!res.ok) {
      const responseBody = await res.json().catch(() => ({}));
      throw new Error(responseBody.error || `HTTP ${res.status}`);
    }

    return await res.json() as T;
  } catch (err: any) {
    clearTimeout(timeoutId);
    if (err.name === 'AbortError') throw new Error('Request timed out');
    throw err;
  }
}

export async function createCareerExperience(payload: ExperienceMutationPayload): Promise<string> {
  const data = await callExperienceApi<{ id: string }>('createExperience', 'POST', payload);
  if (!data.id) {
    throw new Error('Create failed');
  }
  return data.id;
}

export async function updateCareerExperience(
  id: string,
  payload: ExperienceMutationPayload
): Promise<void> {
  await callExperienceApi<{ success: boolean }>('updateExperience', 'POST', {
    id,
    ...payload,
  });
}

export async function deleteCareerExperience(id: string): Promise<void> {
  await callExperienceApi<{ success: boolean }>('deleteExperience', 'POST', { id });
}

export async function extractCareerExperiencesFromConversation(
  conversationId: string
): Promise<{
  createdCount: number;
  createdIds: string[];
  createdExperiences?: Array<{
    id: string;
    title: string;
    type: CareerExperience['type'];
    company: string;
    role: string;
    startDate: string | null;
    endDate: string | null;
  }>;
  model?: string;
  message?: string;
}> {
  return await callExperienceApi<{
    createdCount: number;
    createdIds: string[];
    createdExperiences?: Array<{
      id: string;
      title: string;
      type: CareerExperience['type'];
      company: string;
      role: string;
      startDate: string | null;
      endDate: string | null;
    }>;
    model?: string;
    message?: string;
  }>(
    'extractExperiencesFromConversation',
    'POST',
    { conversationId }
  );
}

export async function previewCareerExperiencesFromConversation(
  conversationId: string
): Promise<{
  extractedExperiences: ProposedCareerExperience[];
  model?: string;
  message?: string;
}> {
  return await callExperienceApi<{
    extractedExperiences: ProposedCareerExperience[];
    model?: string;
    message?: string;
  }>(
    'previewExperiencesFromConversation',
    'POST',
    { conversationId },
    60000
  );
}

export async function previewCareerExperiencesFromResume(): Promise<{
  extractedExperiences: ProposedCareerExperience[];
  model?: string;
  message?: string;
}> {
  return await callExperienceApi<{
    extractedExperiences: ProposedCareerExperience[];
    model?: string;
    message?: string;
  }>(
    'previewExperiencesFromResume',
    'POST',
    undefined,
    60000
  );
}

/**
 * Group experiences into timeline entries by date
 */
export function groupExperiencesByTimeline(experiences: CareerExperience[]): TimelineEntry[] {
  // Group by year-month
  const grouped = new Map<string, CareerExperience[]>();

  experiences.forEach(exp => {
    // Firestore may return null for missing fields; fall back through available dates
    const rawDate = exp.startDate || exp.extractedAt || exp.lastUpdated;
    // `rawDate` may be a Firestore Timestamp-like object ({ _seconds, _nanoseconds })
    // if the field was written as serverTimestamp — convert gracefully.
    let date: string;
    if (!rawDate) {
      date = new Date().toISOString();
    } else if (typeof rawDate === 'string') {
      date = rawDate;
    } else if (typeof (rawDate as any)._seconds === 'number') {
      date = new Date((rawDate as any)._seconds * 1000).toISOString();
    } else {
      date = String(rawDate);
    }
    const yearMonth = date.substring(0, 7); // YYYY-MM

    if (!grouped.has(yearMonth)) {
      grouped.set(yearMonth, []);
    }
    grouped.get(yearMonth)!.push(exp);
  });

  // Convert to timeline entries
  const entries: TimelineEntry[] = [];

  grouped.forEach((exps, yearMonth) => {
    // Find the most significant experience for the entry title
    const primaryExp = exps.find(e => e.type === 'role') || exps[0];

    entries.push({
      date: `${yearMonth}-01`, // Use first of month
      type: primaryExp.type === 'role' ? 'role_change' : 'experience',
      title: primaryExp.company
        ? `${primaryExp.company} - ${primaryExp.role || 'Various Projects'}`
        : primaryExp.title,
      description: `${exps.length} experience${exps.length > 1 ? 's' : ''} documented`,
      experiences: exps,
    });
  });

  // Sort by date descending (newest first)
  entries.sort((a, b) => b.date.localeCompare(a.date));

  return entries;
}

/**
 * Deduplicate proposed experiences against the user's existing career memory.
 * Returns a plan: which to create fresh, which to merge with existing, and internal dups to consolidate.
 */
export async function deduplicateCareerExperiences(
  proposed: ProposedCareerExperience[]
): Promise<DeduplicateResult> {
  return await callExperienceApi<DeduplicateResult>(
    'deduplicateExperiences',
    'POST',
    { proposed },
    60000
  );
}
